import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import Color from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import { Extension } from '@tiptap/core';
import { marked } from 'marked';
import TurndownService from 'turndown';
import { List, ListOrdered, Minus, Plus } from 'lucide-react';

import { parseReportMarkdown } from '../reportView/parseMarkdown';

type CustomReportEditorProps = {
  value: string;
  onChange: (next: string) => void;
};

type ReportBlock = {
  id: string;
  heading: string;
  content: string; // markdown for the block body
};

const DEFAULT_TITLE = 'Custom Analysis Title';
const DEFAULT_HEADING = 'Analysis Section';
const DEFAULT_BODY = '';
const MIN_ZOOM = 80;
const MAX_ZOOM = 150;

const isCustomReportDebugEnabled = () =>
  typeof window !== 'undefined' && window.localStorage?.getItem('ca_custom_report_debug') === '1';

const debugCustomReport = (...args: any[]) => {
  if (!isCustomReportDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.log('[CustomReportDebug]', ...args);
};

const FONT_FAMILIES = [
  { label: 'Default', value: '' },
  { label: 'Inter', value: 'Inter, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times', value: '"Times New Roman", serif' },
  { label: 'Mono', value: '"JetBrains Mono", monospace' },
];

const FONT_SIZE_OPTIONS = [
  { label: 'Default', value: '' },
  { label: '12 px', value: '12px' },
  { label: '14 px', value: '14px' },
  { label: '16 px', value: '16px' },
  { label: '18 px', value: '18px' },
  { label: '24 px', value: '24px' },
  { label: '32 px', value: '32px' },
];

const blockId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `block-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const createBlock = (heading: string = DEFAULT_HEADING, content: string = DEFAULT_BODY): ReportBlock => ({
  id: blockId(),
  heading,
  content,
});

const SECTION_REGEX = /##\s+(.+)\n([\s\S]*?)(?=\n##\s|$)/g;

const parseDocument = (raw: string): { title: string; blocks: ReportBlock[] } => {
  if (!raw || raw.trim().length === 0) {
    return { title: DEFAULT_TITLE, blocks: [createBlock()] };
  }

  const titleMatch = raw.match(/^#\s+(.+)$/m);
  const title = titleMatch ? titleMatch[1].trim() : DEFAULT_TITLE;
  const blocks: ReportBlock[] = [];
  let match: RegExpExecArray | null;

  while ((match = SECTION_REGEX.exec(raw)) !== null) {
    const heading = match[1]?.trim() || DEFAULT_HEADING;
    const body = (match[2] || '').trim();
    blocks.push(createBlock(heading, body.length ? body : DEFAULT_BODY));
  }

  if (blocks.length === 0) {
    blocks.push(createBlock());
  }

  return { title, blocks };
};

const buildDocument = (title: string, blocks: ReportBlock[]): string => {
  const safeTitle = title?.trim().length ? title.trim() : DEFAULT_TITLE;
  const safeBlocks = blocks.length ? blocks : [createBlock()];

  const sections = safeBlocks
    .map((block) => {
      const heading = block.heading?.trim().length ? block.heading.trim() : DEFAULT_HEADING;
      const content = block.content?.trim().length ? block.content.trim() : DEFAULT_BODY;
      return `## ${heading}\n\n${content}`;
    })
    .join('\n\n');

  return `# ${safeTitle}\n\n${sections}\n`;
};

const markdownToHtml = (markdown: string): string => marked.parse(markdown, { async: false }) as string;

/**
 * TipTap extensions shared across editors
 */

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    fontFamily: {
      setFontFamily: (fontFamily?: string | null) => ReturnType;
    };
    fontSize: {
      setFontSize: (fontSize?: string | null) => ReturnType;
    };
  }
}

const FontFamilyExtension = Extension.create({
  name: 'fontFamily',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontFamily: {
            default: null,
            parseHTML: (element) => element.style.fontFamily?.replace(/['"]/g, '') ?? null,
            renderHTML: (attributes) =>
              attributes.fontFamily ? { style: `font-family: ${attributes.fontFamily}` } : {},
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontFamily:
        (fontFamily) =>
        ({ chain }) => {
          if (!fontFamily) {
            return chain().setMark('textStyle', { fontFamily: null }).removeEmptyTextStyle().run();
          }
          return chain().setMark('textStyle', { fontFamily }).run();
        },
    };
  },
});

const FontSizeExtension = Extension.create({
  name: 'fontSize',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize ?? null,
            renderHTML: (attributes) =>
              attributes.fontSize ? { style: `font-size: ${attributes.fontSize}` } : {},
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize:
        (fontSize) =>
        ({ chain }) => {
          if (!fontSize) {
            return chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run();
          }
          return chain().setMark('textStyle', { fontSize }).run();
        },
    };
  },
});

const TIPTAP_EXTENSIONS = [
  StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
  Underline,
  Link.configure({ autolink: true, openOnClick: false }),
  TextAlign.configure({ types: ['heading', 'paragraph'] }),
  Placeholder.configure({ placeholder: 'Write the section narrative…' }),
  TextStyle,
  Color,
  FontFamilyExtension,
  FontSizeExtension,
];

/**
 * Section editor component
 */

type SectionEditorProps = {
  block: ReportBlock;
  zoomLevel: number;
  isActive: boolean;
  convertHtmlToMarkdown: (html: string) => string;
  onHeadingChange: (id: string, heading: string) => void;
  onContentChange: (id: string, markdown: string) => void;
  onActivate: (id: string, editor: Editor | null) => void;
  onSelectionChange: (id: string, editor: Editor | null) => void;
};

const SectionEditor: React.FC<SectionEditorProps> = ({
  block,
  zoomLevel,
  isActive,
  convertHtmlToMarkdown,
  onHeadingChange,
  onContentChange,
  onActivate,
  onSelectionChange,
}) => {
  // Avoid firing onUpdate during programmatic setContent
  const suppressUpdateRef = useRef(false);
  const suppressTimeoutRef = useRef<number | null>(null);
  const updateBurstRef = useRef<{ count: number; start: number }>({ count: 0, start: 0 });

  const editor = useEditor({
    extensions: TIPTAP_EXTENSIONS,
    content: markdownToHtml(block.content),
    onUpdate: ({ editor: instance }) => {
      if (suppressUpdateRef.current) return;

      // Detect runaway update loops and short-circuit to prevent React depth errors
      const now = performance.now();
      if (now - updateBurstRef.current.start > 2000) {
        updateBurstRef.current = { count: 1, start: now };
      } else {
        updateBurstRef.current.count += 1;
      }
      if (updateBurstRef.current.count > 40) {
        console.warn('[CustomReportEditor] Detected excessive editor updates; temporarily suppressing to avoid loops');
        suppressUpdateRef.current = true;
        if (suppressTimeoutRef.current !== null) {
          window.clearTimeout(suppressTimeoutRef.current);
        }
        suppressTimeoutRef.current = window.setTimeout(() => {
          suppressUpdateRef.current = false;
          suppressTimeoutRef.current = null;
          updateBurstRef.current = { count: 0, start: performance.now() };
        }, 0);
        return;
      }

      const markdown = convertHtmlToMarkdown(instance.getHTML());
      // Avoid no-op updates that can cause render loops
      if (markdown !== block.content) {
        debugCustomReport('SectionEditor.onUpdate -> onContentChange', {
          blockId: block.id,
          markdownLength: markdown.length,
          burstCount: updateBurstRef.current.count,
        });
        onContentChange(block.id, markdown);
      } else {
        debugCustomReport('SectionEditor.onUpdate skipped (no content change)', {
          blockId: block.id,
          burstCount: updateBurstRef.current.count,
        });
      }
    },
    onSelectionUpdate: ({ editor: instance }) => {
      if (isActive) {
        onSelectionChange(block.id, instance);
      }
    },
  });

  useEffect(() => {
    if (!editor) return;
    const nextHtml = markdownToHtml(block.content);
    const currentHtml = editor.getHTML();
    // Avoid triggering update loops: only set content when it actually changed
    if (currentHtml !== nextHtml) {
      debugCustomReport('SectionEditor.setContent from block.content', {
        blockId: block.id,
        from: currentHtml.length,
        to: nextHtml.length,
      });
      suppressUpdateRef.current = true;
      editor.commands.setContent(nextHtml, false);
      // Delay re-enabling updates to avoid onUpdate firing from setContent
      if (suppressTimeoutRef.current !== null) {
        window.clearTimeout(suppressTimeoutRef.current);
      }
      suppressTimeoutRef.current = window.setTimeout(() => {
        suppressUpdateRef.current = false;
        suppressTimeoutRef.current = null;
      }, 0);
    }
  }, [block.content, editor]);

  useEffect(() => {
    return () => {
      if (suppressTimeoutRef.current !== null) {
        window.clearTimeout(suppressTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isActive) {
      onActivate(block.id, editor ?? null);
    }
  }, [block.id, editor, isActive, onActivate]);

  return (
    <section className="report-section report-section-editable">
      <div className="section-header">
        <input
          className="section-heading-input"
          value={block.heading}
          onChange={(event) => onHeadingChange(block.id, event.target.value)}
          placeholder="Section heading"
        />
        <div className="section-header-line" />
      </div>
      <div className="section-body">
        <div className="report-rich-editor" style={{ fontSize: `${zoomLevel / 100}em` }}>
          {editor ? (
            <EditorContent
              editor={editor}
              onClick={() => onActivate(block.id, editor)}
              onFocus={() => onActivate(block.id, editor)}
            />
          ) : (
            <div className="tiptap-editor-loading">Loading editor…</div>
          )}
        </div>
      </div>
    </section>
  );
};

/**
 * Main editor component
 */

const sanitizeMarkdown = (markdown: string) =>
  markdown
    .replace(/<\/?[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();

const CustomReportEditor: React.FC<CustomReportEditorProps> = ({ value, onChange }) => {
  const initialDocRef = useRef(parseDocument(value));
  const lastSyncedDocRef = useRef(value);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const [titleValue, setTitleValue] = useState(initialDocRef.current.title);
  const [blocks, setBlocks] = useState<ReportBlock[]>(initialDocRef.current.blocks);
  const [zoomLevel, setZoomLevel] = useState<number>(100);
  const [activeBlockId, setActiveBlockId] = useState<string>(initialDocRef.current.blocks[0]?.id ?? '');
  const [activeEditorVersion, setActiveEditorVersion] = useState(0);
  const activeEditorRef = useRef<Editor | null>(null);

  const turndownService = useMemo(() => {
    const service = new TurndownService({
      headingStyle: 'atx',
      bulletListMarker: '-',
      codeBlockStyle: 'fenced',
    });
    service.addRule('alignedParagraph', {
      filter: (node: HTMLElement) =>
        node?.nodeName === 'P' && Boolean(node.getAttribute?.('style')?.includes('text-align')),
      replacement: (content: string) => `${content}\n\n`,
    });
    service.addRule('spanStripper', {
      filter: (node: HTMLElement) => node?.nodeName === 'SPAN',
      replacement: (content: string) => content,
    });
    service.addRule('fontStripper', {
      filter: (node: HTMLElement) => ['FONT', 'DIV'].includes(node?.nodeName || ''),
      replacement: (content: string) => content,
    });
    return service;
  }, []);

  const convertHtmlToMarkdown = useCallback(
    (html: string) => {
      const markdown = turndownService.turndown(html);
      return sanitizeMarkdown(markdown);
    },
    [turndownService]
  );

  const reportStats = useMemo(() => {
    const doc = buildDocument(titleValue, blocks);
    const parsed = parseReportMarkdown(doc);
    const wordCount = doc
      .replace(/[#*_`>\-\n]/g, ' ')
      .split(/\s+/)
      .filter(Boolean).length;
    return {
      sectionCount: parsed.sections.length || blocks.length,
      wordCount,
    };
  }, [titleValue, blocks]);

  useEffect(() => {
    if (value === lastSyncedDocRef.current) {
      debugCustomReport('CustomReportEditor.value effect ignored (no change)', {
        valueLength: value?.length || 0,
      });
      return;
    }
    const parsed = parseDocument(value);
    debugCustomReport('CustomReportEditor.value effect applied', {
      valueLength: value.length,
      blocks: parsed.blocks.length,
    });
    setTitleValue(parsed.title);
    setBlocks(parsed.blocks);
    setActiveBlockId(parsed.blocks[0]?.id ?? '');
    // Keep lastSyncedDocRef aligned with the parsed document, not the raw value string,
    // so downstream sync comparisons are stable.
    lastSyncedDocRef.current = buildDocument(parsed.title, parsed.blocks);
  }, [value]);

  // Avoid triggering parent state updates on initial mount to prevent render loops
  const hasSyncedInitialRef = useRef(false);
  useEffect(() => {
    const nextDoc = buildDocument(titleValue, blocks);
    if (!hasSyncedInitialRef.current) {
      hasSyncedInitialRef.current = true;
      lastSyncedDocRef.current = nextDoc;
      debugCustomReport('CustomReportEditor.sync effect first-run', {
        nextDocLength: nextDoc.length,
      });
      return;
    }
    if (nextDoc === lastSyncedDocRef.current) {
      debugCustomReport('CustomReportEditor.sync effect skipped (unchanged)', {
        nextDocLength: nextDoc.length,
      });
      return;
    }
    lastSyncedDocRef.current = nextDoc;
    debugCustomReport('CustomReportEditor.sync effect -> onChange', {
      nextDocLength: nextDoc.length,
      blocks: blocks.length,
    });
    onChangeRef.current?.(nextDoc);
  }, [titleValue, blocks]);

  const adjustZoom = (delta: number) => {
    setZoomLevel((prev) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, prev + delta)));
  };

  const setActiveEditor = useCallback((blockId: string, editor: Editor | null) => {
    setActiveBlockId(blockId);
    activeEditorRef.current = editor;
    setActiveEditorVersion((prev) => prev + 1);
  }, []);

  const handleSelectionChange = useCallback(
    (blockId: string, editor: Editor | null) => {
      if (blockId === activeBlockId) {
        activeEditorRef.current = editor;
        setActiveEditorVersion((prev) => prev + 1);
      }
    },
    [activeBlockId]
  );

  const runOnActiveEditor = useCallback((action: (editor: Editor) => void) => {
    const editor = activeEditorRef.current;
    if (!editor) return;
    action(editor);
    setActiveEditorVersion((prev) => prev + 1);
  }, []);

  const currentTextStyle = useMemo(() => activeEditorRef.current?.getAttributes('textStyle') ?? {}, [activeEditorVersion]);

  const normalizeColorValue = (value?: string) => {
    if (!value) return '#000000';
    const hexMatch = value.match(/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i);
    return hexMatch ? value : '#000000';
  };

  const fontFamilyValue: string = currentTextStyle.fontFamily || '';
  const fontSizeValue: string = currentTextStyle.fontSize || '';
  const colorValue: string = normalizeColorValue(currentTextStyle.color);

  const headingValue = useMemo(() => {
    const editor = activeEditorRef.current;
    if (!editor) return 'paragraph';
    if (editor.isActive('heading', { level: 1 })) return 'h1';
    if (editor.isActive('heading', { level: 2 })) return 'h2';
    if (editor.isActive('heading', { level: 3 })) return 'h3';
    return 'paragraph';
  }, [activeEditorVersion]);

  const textButtons = useMemo(
    () => [
      {
        label: 'B',
        active: activeEditorRef.current?.isActive('bold'),
        onClick: () => runOnActiveEditor((editor) => editor.chain().focus().toggleBold().run()),
      },
      {
        label: 'I',
        active: activeEditorRef.current?.isActive('italic'),
        onClick: () => runOnActiveEditor((editor) => editor.chain().focus().toggleItalic().run()),
      },
      {
        label: 'U',
        active: activeEditorRef.current?.isActive('underline'),
        onClick: () => runOnActiveEditor((editor) => editor.chain().focus().toggleUnderline().run()),
      },
      {
        label: 'S',
        active: activeEditorRef.current?.isActive('strike'),
        onClick: () => runOnActiveEditor((editor) => editor.chain().focus().toggleStrike().run()),
      },
    ],
    [activeEditorVersion, runOnActiveEditor]
  );

  const textAlignValue = useMemo(() => {
    const editor = activeEditorRef.current;
    if (!editor) return 'left';
    if (editor.isActive({ textAlign: 'center' })) return 'center';
    if (editor.isActive({ textAlign: 'right' })) return 'right';
    if (editor.isActive({ textAlign: 'justify' })) return 'justify';
    return 'left';
  }, [activeEditorVersion]);

  const handleFontFamilyChange = (next: string) => {
    runOnActiveEditor((editor) => {
      const value = next || null;
      editor.chain().focus().setFontFamily(value).run();
    });
  };

  const handleFontSizeChange = (next: string) => {
    runOnActiveEditor((editor) => {
      const value = next || null;
      editor.chain().focus().setFontSize(value).run();
    });
  };

  const handleColorChange = (next: string) => {
    if (!next) return;
    runOnActiveEditor((editor) => {
      editor.chain().focus().setColor(next).run();
    });
  };

  const handleClearColor = () => {
    runOnActiveEditor((editor) => {
      editor.chain().focus().unsetColor().run();
    });
  };

  const handleHeadingDropdownChange = (value: string) => {
    runOnActiveEditor((editor) => {
      const chain = editor.chain().focus();
      if (value === 'paragraph') {
        chain.setParagraph().run();
      } else {
        const level = Number(value.replace('h', '')) as 1 | 2 | 3;
        chain.setHeading({ level }).run();
      }
    });
  };

  const handleListToggle = (type: 'bullet' | 'ordered') => {
    runOnActiveEditor((editor) => {
      const chain = editor.chain().focus();
      if (type === 'bullet') {
        chain.toggleBulletList().run();
      } else {
        chain.toggleOrderedList().run();
      }
    });
  };

  const handleTextAlignChange = (alignment: 'left' | 'center' | 'right' | 'justify') => {
    runOnActiveEditor((editor) => {
      editor.chain().focus().setTextAlign(alignment).run();
    });
  };

  const handleAddBlock = () => {
    const newBlock = createBlock(`${DEFAULT_HEADING} ${blocks.length + 1}`, DEFAULT_BODY);
    setBlocks((prev) => [...prev, newBlock]);
    setActiveBlockId(newBlock.id);
    setActiveEditorVersion((prev) => prev + 1);
  };

  const handleBlockHeadingChange = (id: string, heading: string) => {
    setBlocks((prev) =>
      prev.map((block) => {
        if (block.id !== id) return block;
        if (block.heading === heading) return block; // no-op
        return { ...block, heading };
      })
    );
  };

  const handleBlockContentChange = (id: string, markdown: string) => {
    setBlocks((prev) =>
      prev.map((block) => {
        if (block.id !== id) return block;
        if (block.content === markdown) return block; // no-op
        return { ...block, content: markdown };
      })
    );
  };

  const toolbarDisabled = !activeBlockId || !activeEditorRef.current;

  return (
    <div className="custom-editor">
      <div className="modern-editor-toolbar">
        <div className="modern-toolbar-group modern-toolbar-zoom">
          <button type="button" onClick={() => adjustZoom(-10)} className="modern-toolbar-button" disabled={toolbarDisabled}>
            <Minus size={16} />
          </button>
          <span className="modern-toolbar-zoom-value">{zoomLevel}%</span>
          <button type="button" onClick={() => adjustZoom(10)} className="modern-toolbar-button" disabled={toolbarDisabled}>
            <Plus size={16} />
          </button>
        </div>
        <div className="modern-toolbar-group">
          <select
            className="modern-toolbar-select"
            value={fontFamilyValue}
            onChange={(event) => handleFontFamilyChange(event.target.value)}
            disabled={toolbarDisabled}
          >
            {FONT_FAMILIES.map((option) => (
              <option key={option.value || 'default'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            className="modern-toolbar-select"
            value={fontSizeValue}
            onChange={(event) => handleFontSizeChange(event.target.value)}
            disabled={toolbarDisabled}
          >
            {FONT_SIZE_OPTIONS.map((size) => (
              <option key={size.label} value={size.value}>
                {size.label}
              </option>
            ))}
          </select>
          <div className="modern-toolbar-divider" />
          <label className="modern-toolbar-color">
            <input
              type="color"
              value={colorValue}
              onChange={(event) => handleColorChange(event.target.value)}
              disabled={toolbarDisabled}
            />
          </label>
          <button type="button" className="modern-toolbar-button" onClick={handleClearColor} disabled={toolbarDisabled}>
            Reset
          </button>
        </div>
        <div className="modern-toolbar-group">
          <select
            className="modern-toolbar-select"
            value={headingValue}
            onChange={(event) => handleHeadingDropdownChange(event.target.value)}
            disabled={toolbarDisabled}
          >
            <option value="paragraph">Paragraph</option>
            <option value="h1">Heading 1</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
          </select>
          <div className="modern-toolbar-divider" />
          {textButtons.map((button) => (
            <button
              key={button.label}
              type="button"
              className={`modern-toolbar-button ${button.active ? 'modern-toolbar-button-active' : ''}`}
              onClick={button.onClick}
              disabled={toolbarDisabled}
            >
              {button.label}
            </button>
          ))}
          <div className="modern-toolbar-divider" />
          <button
            type="button"
            className={`modern-toolbar-button ${
              activeEditorRef.current?.isActive('bulletList') ? 'modern-toolbar-button-active' : ''
            }`}
            onClick={() => handleListToggle('bullet')}
            disabled={toolbarDisabled}
          >
            <List size={16} />
          </button>
          <button
            type="button"
            className={`modern-toolbar-button ${
              activeEditorRef.current?.isActive('orderedList') ? 'modern-toolbar-button-active' : ''
            }`}
            onClick={() => handleListToggle('ordered')}
            disabled={toolbarDisabled}
          >
            <ListOrdered size={16} />
          </button>
        </div>
        <div className="modern-toolbar-group">
          {['left', 'center', 'right', 'justify'].map((alignment) => (
            <button
              key={alignment}
              type="button"
              className={`modern-toolbar-button ${textAlignValue === alignment ? 'modern-toolbar-button-active' : ''}`}
              onClick={() => handleTextAlignChange(alignment as 'left' | 'center' | 'right' | 'justify')}
              disabled={toolbarDisabled}
            >
              {alignment.charAt(0).toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      <div className="custom-editor-body">
        <div className="interactive-report custom-report-editable">
          <article className="report-page">
            <header className="report-header">
              <div className="report-title-group">
                <span className="report-eyebrow">Custom Analysis</span>
                <input
                  className="report-title-input"
                  value={titleValue}
                  onChange={(event) => setTitleValue(event.target.value)}
                  placeholder="Enter analysis title"
                />
              </div>
              <div className="report-meta">
                <div className="report-meta-card">
                  <span className="meta-label">Sections</span>
                  <span className="meta-value">{reportStats.sectionCount}</span>
                </div>
                <div className="report-meta-card">
                  <span className="meta-label">Words</span>
                  <span className="meta-value">{reportStats.wordCount}</span>
                </div>
              </div>
            </header>
            <div className="report-divider" />
            <div className="report-sections">
              {blocks.map((block) => (
                <SectionEditor
                  key={block.id}
                  block={block}
                  zoomLevel={zoomLevel}
                  isActive={activeBlockId === block.id}
                  convertHtmlToMarkdown={convertHtmlToMarkdown}
                  onHeadingChange={handleBlockHeadingChange}
                  onContentChange={handleBlockContentChange}
                  onActivate={setActiveEditor}
                  onSelectionChange={handleSelectionChange}
                />
              ))}
            </div>
            <button type="button" className="report-add-block" onClick={handleAddBlock}>
              <Plus size={16} />
              Add new section
            </button>
          </article>
        </div>
      </div>
    </div>
  );
};

export default CustomReportEditor;
